/**
 * Kit de telas da Werc 2.0 (F3) — peças pequenas em cima da UI do Macro, para
 * portar as telas do app atual com o mesmo padrão: campos com erro, seleção,
 * etiqueta colorida, layout "lista + detalhe" (painel redimensionável; gaveta no
 * celular), estado vazio e confirmação.
 */
import { createSignal, type JSX, Show, splitProps } from 'solid-js'
import { Dialog } from '@ui/components/Dialog'
import { Resize } from '@core/component/Resize'
import { isMobileWidth } from '@core/mobile/mobileWidth'
import { confirmDialog } from '@ui/components/ConfirmDialog'
import { cn } from '@ui/utils/classname'

/** Rótulo + campo + erro/dica (o "Field" do app atual). */
export function Campo(props: { rotulo: JSX.Element; erro?: string | null; dica?: string; class?: string; children: JSX.Element }) {
  return (
    <label class={`flex flex-col gap-1 text-xs text-ink-muted min-w-0 ${props.class ?? ''}`}>
      <span>{props.rotulo}</span>
      {props.children}
      <Show when={props.erro}>
        <span class="text-failure">{props.erro}</span>
      </Show>
      <Show when={!props.erro && props.dica}>
        <span class="text-ink-placeholder">{props.dica}</span>
      </Show>
    </label>
  )
}

const CONTROLE = 'h-8 w-full rounded-md border border-edge bg-input px-2 text-sm text-ink disabled:opacity-60'

/** Seleção nativa no visual dos campos do Macro. */
export function Selecao(props: JSX.SelectHTMLAttributes<HTMLSelectElement>) {
  const [l, resto] = splitProps(props, ['class'])
  return <select class={cn(CONTROLE, l.class)} {...resto} />
}

export function AreaTexto(props: JSX.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const [l, resto] = splitProps(props, ['class'])
  return <textarea class={cn('min-h-20 w-full rounded-md border border-edge bg-input px-2 py-1.5 text-sm text-ink', l.class)} {...resto} />
}

export function Marcar(props: { checked: boolean; onChange: (v: boolean) => void; children: JSX.Element; disabled?: boolean }) {
  return (
    <label class="inline-flex items-center gap-2 text-sm text-ink-muted select-none" classList={{ 'opacity-60': props.disabled }}>
      <input
        type="checkbox"
        class="size-4 accent-[var(--color-accent)]"
        checked={props.checked}
        disabled={props.disabled}
        onChange={(e) => props.onChange(e.currentTarget.checked)}
      />
      {props.children}
    </label>
  )
}

export type Tom = 'neutro' | 'sucesso' | 'aviso' | 'perigo' | 'destaque'
const TONS: Record<Tom, string> = {
  neutro: 'border-edge text-ink-muted',
  sucesso: 'border-success/40 bg-success/10 text-success',
  aviso: 'border-warning/40 bg-warning/10 text-warning',
  perigo: 'border-failure/40 bg-failure/10 text-failure',
  destaque: 'border-accent/40 bg-accent/10 text-accent',
}
export function Etiqueta(props: { tom?: Tom; children: JSX.Element; title?: string }) {
  return (
    <span class={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium whitespace-nowrap ${TONS[props.tom ?? 'neutro']}`} title={props.title}>
      {props.children}
    </span>
  )
}

export function Vazio(props: { titulo: string; texto?: string }) {
  return (
    <div class="py-12 text-center">
      <div class="text-sm font-medium text-ink">{props.titulo}</div>
      <Show when={props.texto}>
        <div class="text-xs text-ink-subtle mt-1">{props.texto}</div>
      </Show>
    </div>
  )
}

export function Erro(props: { mensagem: string | null | undefined }) {
  return (
    <Show when={props.mensagem}>
      <p class="rounded-md border border-failure/40 bg-failure/10 px-3 py-2 text-sm text-failure" role="alert">
        {props.mensagem}
      </p>
    </Show>
  )
}

/** Título de seção pequeno (maiúsculas), como no app atual. */
export function Secao(props: { titulo: string; children: JSX.Element; acao?: JSX.Element }) {
  return (
    <section class="flex flex-col gap-2">
      <div class="flex items-center justify-between gap-2">
        <h3 class="text-xs font-medium uppercase tracking-wide text-ink-subtle">{props.titulo}</h3>
        {props.acao}
      </div>
      {props.children}
    </section>
  )
}

/**
 * Layout "lista + detalhe": no computador, o detalhe abre num painel ao lado
 * (arrastar a divisão muda a largura); no celular, numa gaveta.
 */
export function ListaDetalhe(props: {
  id: string
  lista: JSX.Element
  detalhe: JSX.Element | false | null | undefined
  aoFecharDetalhe: () => void
  larguraDetalhe?: number
}) {
  return (
    <div class="h-full relative">
      <Show when={!isMobileWidth()} fallback={<div class="h-full overflow-auto">{props.lista}</div>}>
        <Resize.Zone direction="horizontal" gutter={8} minSize={300}>
          <Resize.Panel id={`${props.id}-lista`} minSize={420}>
            <div class="h-full overflow-auto">{props.lista}</div>
          </Resize.Panel>
          <Show when={props.detalhe}>
            <Resize.Panel id={`${props.id}-detalhe`} minSize={360} maxSize={900} target={{ kind: 'px', px: props.larguraDetalhe ?? 480 }}>
              <div class="h-full overflow-hidden border-l border-edge bg-panel">{props.detalhe}</div>
            </Resize.Panel>
          </Show>
        </Resize.Zone>
      </Show>
      <Show when={isMobileWidth()}>
        <Dialog open={!!props.detalhe} onOpenChange={(o) => !o && props.aoFecharDetalhe()} class="w-full">
          <div class="h-[85dvh] overflow-hidden">{props.detalhe}</div>
        </Dialog>
      </Show>
    </div>
  )
}

/** Cabeçalho + corpo rolável + rodapé de um painel de detalhe. */
export function Painel(props: { titulo: JSX.Element; sub?: JSX.Element; aoFechar: () => void; children: JSX.Element; rodape?: JSX.Element }) {
  return (
    <div class="h-full flex flex-col">
      <div class="px-5 pt-4 pb-3 border-b border-edge flex items-start gap-3">
        <div class="min-w-0 flex-1">
          <div class="font-semibold text-ink">{props.titulo}</div>
          <Show when={props.sub}>
            <div class="text-xs text-ink-subtle mt-0.5">{props.sub}</div>
          </Show>
        </div>
        <button type="button" class="text-sm text-ink-subtle hover:text-ink px-2 py-1 rounded hover:bg-hover" onClick={props.aoFechar}>
          Fechar
        </button>
      </div>
      <div class="flex-1 min-h-0 overflow-auto px-5 py-4 flex flex-col gap-5">{props.children}</div>
      <Show when={props.rodape}>
        <div class="px-5 py-3 border-t border-edge flex flex-wrap items-center gap-2">{props.rodape}</div>
      </Show>
    </div>
  )
}

/** Diálogo de formulário com título e largura padrão. */
export function Formulario(props: {
  aberto: boolean
  titulo: string
  aoFechar: () => void
  aoEnviar: (e: SubmitEvent) => void
  largura?: number
  children: JSX.Element
  rodape: JSX.Element
}) {
  return (
    <Dialog open={props.aberto} onOpenChange={(o) => !o && props.aoFechar()} class="w-auto max-w-[calc(100vw-32px)]">
      <form
        onSubmit={(e) => {
          e.preventDefault()
          props.aoEnviar(e)
        }}
        class="flex flex-col gap-4 p-6 max-h-[88dvh] overflow-auto"
        style={{ width: `min(${props.largura ?? 720}px, calc(100vw - 32px))` }}
      >
        <h2 class="text-lg font-semibold text-ink">{props.titulo}</h2>
        {props.children}
        <div class="flex justify-end gap-2 pt-1">{props.rodape}</div>
      </form>
    </Dialog>
  )
}

/** Substitui o `confirm()` do app atual (diálogo do Macro, gaveta no celular). */
export function confirmar(titulo: string, texto: string, opcoes: { rotulo?: string; perigo?: boolean } = {}): Promise<boolean> {
  return confirmDialog({ title: titulo, body: texto, confirmLabel: opcoes.rotulo ?? 'Confirmar', cancelLabel: 'Cancelar', tone: opcoes.perigo ? 'danger' : 'default' })
}

/** Sinal de "recarregar" para recursos (somar 1 a cada gravação). */
export function criarRecarga() {
  const [n, setN] = createSignal(0)
  return [n, () => setN((x) => x + 1)] as const
}

/** "1.234,5" / "1234.5" → número; vazio → null; inválido → NaN (mesma regra do app atual). */
export function lerNumero(s: string): number | null {
  const t = s.trim()
  if (t === '') return null
  const normal = /,\d*$/.test(t) ? t.replace(/\./g, '').replace(',', '.') : t
  const n = Number(normal)
  return Number.isFinite(n) ? n : NaN
}

/** Formatos pt-BR iguais aos do app atual (src/lib/format.ts). */
export function formatarDoc(doc: string | null, tipo?: 'PJ' | 'PF'): string {
  if (!doc) return '—'
  const d = doc.replace(/\D/g, '')
  if (d.length === 14 || tipo === 'PJ') return d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2}).*/, '$1.$2.$3/$4-$5')
  if (d.length === 11 || tipo === 'PF') return d.replace(/^(\d{3})(\d{3})(\d{3})(\d{2}).*/, '$1.$2.$3-$4')
  return d
}
export const brl = (n: number | null | undefined) => (n == null ? '—' : n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }))
export const num = (n: number | null | undefined) => (n == null ? '—' : n.toLocaleString('pt-BR'))
export const dataBR = (iso: string | null | undefined) => (iso ? iso.slice(0, 10).split('-').reverse().join('/') : '—')

/** Classes de tabela no padrão das telas (cabeçalho fixo, linhas com hover). */
export const T = {
  caixa: 'rounded-xl border border-edge bg-panel overflow-auto',
  tabela: 'w-full text-sm',
  cab: 'text-left text-ink-subtle border-b border-edge sticky top-0 bg-panel z-10',
  th: 'font-medium px-3 py-2 whitespace-nowrap',
  tr: 'border-b border-edge-muted last:border-0 hover:bg-hover',
  td: 'px-3 py-2',
}
